import React, { forwardRef, useContext } from 'react';

import { GroupCard } from 'components/organisms/Teams';
import { useUser2 } from 'contexts/user2';
import { PageContext } from 'pages/Teams/PageProvider';
import { BottomBar, Button, Divider, Icon, IconButton, Stack, Text, TextInput, Tooltip } from 'shell/components';
import { getUserPreferredFullName } from 'utils/helper';

const ConfirmationStep = forwardRef(
    ({ filters, setFilters, groups, onClickBack, onClickDownload, onClickEdit, onClickConfirm }, ref) => {
        const { studentAccountUsers } = useUser2();

        const { isWSLoading } = useContext(PageContext);

        const unassignedStudents = studentAccountUsers.filter((accountUser) => {
            if (groups.some((group) => group.members.some((member) => member.user_id === accountUser.user_id))) {
                return false;
            }
            return true;
        });

        return (
            <>
                <Stack ref={ref} divider={<Divider />} spacing={5}>
                    <Stack spacing={4}>
                        <IconButton kind={'arrowLeft'} size={'medium'} variant={'square'} onClick={onClickBack} />
                        <Text variant={'h6'} weight={'semibold'}>
                            Please Confirm Your Teams
                        </Text>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={2}>
                            <TextInput
                                sx={{ width: 'unset' }}
                                disablePointerEventsOnIcon={!Boolean(filters.searchInput)}
                                endIcon={
                                    filters.searchInput ? (
                                        <IconButton
                                            aria-label={'clear search input'}
                                            kind={'exit'}
                                            variant={'square'}
                                            onClick={() => setFilters((current) => ({ ...current, searchInput: '' }))}
                                            onMouseDown={(e) => e.preventDefault()}
                                        />
                                    ) : (
                                        <Icon kind={'search'} />
                                    )
                                }
                                placeholder={'Search'}
                                value={filters.searchInput}
                                onChange={(e) => setFilters((current) => ({ ...current, searchInput: e.target.value }))}
                            />
                            <Button
                                sx={{ flexShrink: 0 }}
                                startIcon={<Icon kind={'download'} />}
                                variant={'tertiary'}
                                onClick={onClickDownload}
                            >
                                Download Submissions
                            </Button>
                        </Stack>
                    </Stack>
                    {unassignedStudents.length ? (
                        <Stack spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text variant={'h6'} weight={'semibold'}>
                                    Unassigned Students ({unassignedStudents.length})
                                </Text>
                                <Tooltip
                                    content={
                                        'These are students that did not fill out the criteria-based questionnaire before teams were generated. You can manually assign these students to teams by clicking “Edit Teams”.'
                                    }
                                    type={'info'}
                                />
                            </Stack>
                            <Stack
                                sx={{ flexWrap: 'wrap' }}
                                direction={'row'}
                                divider={
                                    <Text color={'disabled'} variant={'p2'}>
                                        /
                                    </Text>
                                }
                                spacing={1}
                            >
                                {unassignedStudents.map((student) => (
                                    <Text key={student.user_id} color={'light'} variant={'p2'}>
                                        {getUserPreferredFullName(student)}
                                    </Text>
                                ))}
                            </Stack>
                        </Stack>
                    ) : null}
                    <Stack spacing={4}>
                        {groups
                            .filter((group) => {
                                if (group.name.toLowerCase().includes(filters.searchInput.toLowerCase())) return true;
                                if (
                                    group.members
                                        .map((member) => getUserPreferredFullName(member))
                                        .some((name) => name.toLowerCase().includes(filters.searchInput.toLowerCase()))
                                )
                                    return true;
                                return false;
                            })
                            .map((group) => {
                                return (
                                    <GroupCard
                                        key={group.id}
                                        group={group}
                                        members={group.members}
                                        onClick={onClickEdit}
                                    />
                                );
                            })}
                    </Stack>
                </Stack>
                <BottomBar>
                    <Stack direction={'row-reverse'} spacing={2}>
                        <Button loading={isWSLoading} onClick={onClickConfirm}>
                            Confirm Teams
                        </Button>
                        <Button
                            disabled={isWSLoading}
                            startIcon={<Icon kind={'edit'} />}
                            variant={'secondary'}
                            onClick={onClickEdit}
                        >
                            Edit Teams
                        </Button>
                    </Stack>
                </BottomBar>
            </>
        );
    }
);

export default ConfirmationStep;
